import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ColumnSelector from 'notebook/sample-propagation/add-samples-modal/ColumnSelector';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {findMatchingDestinationColumnIndices} from 'processes/tables/column-mapping/findMatchingDestinationColumnIndex';
import getMatchingColumnConstraints from 'processes/tables/column-mapping/getMatchingColumnConstraints';
import {propagateItemsToDestinationTable} from 'processes/tables/column-mapping/propagateContentFromSourceToDestinationTable';
import {CreateProcessButtonQuery} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import CenteredHorizontalThrobber from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';

import {ColumnSummaryQuery, ColumnSummaryQueryVariables} from './__generated/ColumnSummaryQuery';

type ProcessTaskSchema = CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];

const COLUMN_SUMMARY_QUERY = gql`
  query ColumnSummaryQuery($bioentityIds: [StrictString!]!, $storableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    bioentities(ids: $bioentityIds) {
      ...BioentitySummaryOrInaccessibleGraphQL
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        ...StorableSummaryGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('BioentitySummaryOrInaccessible').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('StorableSummary').fragment}
`;

export interface TaskColumnMappingInfoBase {
  columnName: string;
  columnIcon?: BenchlingIconName;
  hasValues: boolean;
}

export interface TaskColumnMappingInfoWithValues extends TaskColumnMappingInfoBase {
  columnName: string;
  columnValues: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;
  columnIcon?: BenchlingIconName;
  hasValues: true;
}

export interface TaskColumnMappingInfoWithIds {
  columnName: string;
  columnType: BaseTypedValueType;
  columnIds: Array<string>;
  columnIcon?: BenchlingIconName;
  hasValues: false;
}

export type TaskColumnMappingInfo = TaskColumnMappingInfoWithValues | TaskColumnMappingInfoWithIds;

export interface ColumnSummary {
  columnName: string;
  columnIcon?: BenchlingIconName;
}

interface SelectTargetColumnModalSubmitResult {
  taskTable: ProcessTaskStructuredTable | null;
  shouldRunCreateProcessModal: boolean;
}

export interface SelectTargetColumnModalProps {
  // Source columns that can be mapped
  sourceColumns: Array<TaskColumnMappingInfo>;
  taskSchema: ProcessTaskSchema;
  taskTable: ProcessTaskStructuredTable;
  selectorsById: SchemaFieldSelectorsDict;
  summariesById: Record<string, BioentitySummary | MinimalStorableSummaryWithSchemaId>;
  onRequestClose: () => void;
  onRequestSubmit: (result: SelectTargetColumnModalSubmitResult) => void;
}

function getSelectableDestinationColumns(
  sourceItems: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>,
  taskTable: ProcessTaskStructuredTable,
  taskSchema: ProcessTaskSchema
): Array<ColumnSummary> {
  assert(sourceItems.length > 0, 'Source column must have at least 1 value');
  const columnConstraints = getMatchingColumnConstraints(sourceItems);
  const destinationColumnIndices = findMatchingDestinationColumnIndices(
    columnConstraints,
    taskSchema,
    taskTable
  );
  return destinationColumnIndices.map((destinationColumnIndex) => {
    const column = taskTable.columns[destinationColumnIndex];
    return {
      columnName: column.name,
    };
  });
}

export function SelectTaskColumnMappingsModal({
  sourceColumns,
  taskSchema,
  taskTable,
  selectorsById,
  summariesById,
  onRequestClose,
  onRequestSubmit,
}: SelectTargetColumnModalProps): JSX.Element {
  const [selectedSourceColumnIndex, setSelectedSourceColumnIndex] = useState<number>(0);
  const [selectedSourceItems, setSelectedSourceItems] = useState<
    Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId> | null
  >(null);
  // Destination columns are dynamic based on the selected source column,
  // so we need to maintain a list of which ones are selectable
  const [selectableDestinationColumns, setSelectableDestinationColumns] =
    useState<Array<ColumnSummary> | null>(null);
  const [selectedDestinationColumnIndex, setSelectedDestinationColumnIndex] = useState<number>(0);

  const handleSubmit = (): void => {
    assert(selectedSourceItems != null, 'Selected source items must not be null');
    assert(selectableDestinationColumns != null, 'Selectable destination columns must not be null');
    onRequestSubmit({
      taskTable: propagateItemsToDestinationTable({
        items: selectedSourceItems,
        destinationColumnIndex: taskTable.columns.findIndex(
          ({name}) => name === selectableDestinationColumns[selectedDestinationColumnIndex].columnName
        ),
        destinationTable: taskTable,
      }),
      shouldRunCreateProcessModal: true,
    });
  };

  useEffect(() => {
    // When a new source column is selected, reset the destination column index
    setSelectedDestinationColumnIndex(0);
    let selectedSourceItems: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId> = [];
    if (sourceColumns[selectedSourceColumnIndex].hasValues) {
      selectedSourceItems = sourceColumns[selectedSourceColumnIndex].columnValues;
    } else {
      selectedSourceItems = sourceColumns[selectedSourceColumnIndex].columnIds.map(
        (id) => summariesById[id]
      ) as Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;
    }
    setSelectedSourceItems(selectedSourceItems);
    setSelectableDestinationColumns(
      getSelectableDestinationColumns(selectedSourceItems, taskTable, taskSchema)
    );
  }, [selectedSourceColumnIndex, sourceColumns, taskSchema, selectorsById, taskTable, summariesById]);

  return (
    <Modal
      title="Map a column to create tasks"
      buttons={[
        {
          text: 'Continue without mapping',
          onClick: () => {
            onRequestSubmit({
              taskTable: null,
              shouldRunCreateProcessModal: true,
            });
          },
        },
        {
          text: 'Map column',
          submit: true,
          disabled: selectableDestinationColumns == null || selectableDestinationColumns.length === 0,
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <span>Choose a column to use when creating tasks. We'll copy its values into the task table.</span>
      <div className={classNames('u-small-spacing-bottom', css(styles.container))}>
        <ColumnSelector
          label="Select a source column"
          columns={sourceColumns.map((column) => ({
            name: column.columnName,
            icon: column.columnIcon,
          }))}
          onChange={setSelectedSourceColumnIndex}
          selectedColumnIndex={selectedSourceColumnIndex}
          disableWhenOnlyOneColumn={false}
        />
      </div>
      <hr />
      {selectableDestinationColumns == null || selectedSourceItems == null ? (
        <CenteredHorizontalThrobber />
      ) : selectableDestinationColumns.length > 0 ? (
        <>
          <span>Choose where this data should go in the task table.</span>
          <div className={classNames('u-small-spacing-bottom', css(styles.container))}>
            <ColumnSelector
              label="Select a destination column"
              columns={selectableDestinationColumns.map((column) => ({
                name: column.columnName,
                icon: column.columnIcon,
              }))}
              onChange={setSelectedDestinationColumnIndex}
              selectedColumnIndex={selectedDestinationColumnIndex}
              disableWhenOnlyOneColumn={false}
            />
          </div>
        </>
      ) : (
        <span>Select a source column with mappable destination columns.</span>
      )}
    </Modal>
  );
}

export interface QueryingSelectTaskColumnMappingsModalProps {
  sourceColumns: Array<TaskColumnMappingInfo>;
  taskSchema: ProcessTaskSchema;
  taskTable: ProcessTaskStructuredTable;
  selectorsById: SchemaFieldSelectorsDict;
  onRequestClose: () => void;
  onRequestSubmit: (result: SelectTargetColumnModalSubmitResult) => void;
}

export default function QueryingSelectTaskColumnMappingsModal(
  props: QueryingSelectTaskColumnMappingsModalProps
): JSX.Element | null {
  const bioentityIds = props.sourceColumns
    .filter((column) => !column.hasValues && column.columnType === BaseTypedValueType.BIOENTITY)
    .map((column) => (!column.hasValues ? column.columnIds : []))
    .flat();
  const storableIds = props.sourceColumns
    .filter((column) => !column.hasValues && column.columnType === BaseTypedValueType.STORABLES)
    .map((column) => (!column.hasValues ? column.columnIds : []))
    .flat();
  const result = useSkippableBenchlingQuery<ColumnSummaryQuery, ColumnSummaryQueryVariables>(
    COLUMN_SUMMARY_QUERY,
    {
      variables: {
        bioentityIds,
        storableIds,
      },
    }
  );
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const summariesById: Record<string, BioentitySummary | MinimalStorableSummaryWithSchemaId> = {};
    result.data.bioentities.forEach((bioentity) => {
      if (!isInaccessible(bioentity)) {
        summariesById[bioentity.id] = graphQLToREST('BioentitySummary', bioentity);
      }
    });
    result.data.storables.forEach((storable) => {
      if (!isInaccessible(storable)) {
        summariesById[storable.id] = storable;
      }
    });
    return <SelectTaskColumnMappingsModal {...props} summariesById={summariesById} />;
  } else {
    return null;
  }
}

const styles = StyleSheet.create({
  container: {
    minWidth: 400,
  },
});
